import type { StorefrontProduct } from '../lib/dropify'

export type ProductLandingPilot = {
  slug: string
  id: string
  category: string
  parentHref: string
  parentLabel: string
  eyebrow: string
  summary: string
  buyingNote: string
  facts: Array<{ label: string; value: string }>
  fitTitle: string
  fit: string[]
  checkTitle: string
  checks: string[]
  detailLabel: string
  detailPlaceholder: string
  messageDetail: string
  messageRequest: string
  unit: 'lusin' | 'box' | null
  quantityPlaceholder: string
  alternatives: Array<{ label: string; href: string; description: string; itemId?: string; itemCategory?: string; price?: string | null }>
  faq: Array<{ q: string; a: string }>
}

const PILOTS: Record<string, ProductLandingPilot> = {
  'MANSHET-GEKKO-HITAM': {
    slug: 'sarung-tangan-gekko-grip-nitrile-hitam',
    id: '77528591-8da4-4b5d-bba7-6ba899bd2ddf',
    category: 'Sarung tangan kerja',
    parentHref: '/sarung-tangan-gekko',
    parentLabel: 'Sarung tangan Gekko Grip',
    eyebrow: 'Grosir sarung tangan kerja',
    summary: 'Liner polyester hitam dengan lapisan nitrile pada telapak dan jari. Untuk pekerjaan bengkel, otomotif, dan perawatan mesin yang membutuhkan grip saat menangani komponen berminyak.',
    buyingNote: 'Minimum order 50 lusin (600 pasang). Satu lusin berisi 12 pasang.',
    facts: [
      { label: 'Lapisan telapak', value: 'Nitrile hitam' },
      { label: 'Liner', value: 'Polyester hitam' },
      { label: 'Satuan', value: 'Lusin, 12 pasang' },
      { label: 'Minimum order', value: '50 lusin' },
    ],
    fitTitle: 'Untuk pekerjaan apa?',
    fit: ['Bengkel dan perawatan mesin', 'Handling komponen dengan permukaan berminyak', 'Pekerjaan mekanikal dan area kerja kotor'],
    checkTitle: 'Periksa sebelum PO',
    checks: [
      'Sebutkan ukuran dan jumlah per ukuran yang dibutuhkan. Admin akan cek ketersediaannya.',
      'Jika tim K3 mewajibkan standar tertentu, kirim syaratnya agar marking dan dokumen untuk seri ini dapat dicocokkan.',
      'Untuk risiko bahan kimia, sayatan, panas, atau listrik, minta rekomendasi seri yang sesuai sebelum digunakan.',
    ],
    detailLabel: 'Jenis pekerjaan atau ukuran yang dicari',
    detailPlaceholder: 'Contoh: bengkel, ukuran 9 dan 10',
    messageDetail: 'Pemakaian atau ukuran',
    messageRequest: 'Mohon cek ukuran, stok aktual, dokumen yang tersedia, dan quotation.',
    unit: 'lusin',
    quantityPlaceholder: 'Contoh: 50',
    alternatives: [
      { label: 'Gekko Grip Nitrile Biru', href: '/produk/sarung-tangan-gekko-grip-nitrile-biru', description: 'Bandingkan warna dan kebutuhan kerja pada seri nitrile.' },
      { label: 'Lini Gekko Grip', href: '/sarung-tangan-gekko', description: 'Lihat seri nitrile dan PU sebelum menentukan model.' },
    ],
    faq: [
      { q: 'Berapa minimum order Gekko Grip Nitrile Hitam?', a: 'Minimum order 50 lusin atau 600 pasang. Satu lusin berisi 12 pasang. Harga mengikuti jumlah dan stok saat quotation dibuat.' },
      { q: 'Apakah ukuran dan dokumen keselamatan bisa dicek?', a: 'Bisa. Kirim ukuran yang dibutuhkan dan standar wajib dari tim K3. Admin akan memeriksa ukuran, marking, serta dokumen yang tersedia untuk seri ini sebelum PO.' },
      { q: 'Apakah seri ini untuk bahan kimia atau pekerjaan listrik?', a: 'Kecocokan terhadap bahan kimia dan risiko listrik perlu diperiksa dari dokumen seri yang tepat. Sampaikan kondisi kerjanya kepada admin sebelum memilih sarung tangan.' },
      { q: 'Bagaimana mengecek stok ukuran Gekko Grip Nitrile Hitam?', a: 'Kirim ukuran dan jumlah per ukuran yang dibutuhkan. Admin akan memeriksa stok aktual dan mengirim foto barang yang tersedia sebelum quotation dibuat.' },
      { q: 'Apa yang perlu dikirim untuk meminta quotation?', a: 'Sebutkan jenis pekerjaan, jumlah dalam lusin, ukuran yang dicari, standar wajib bila ada, dan kota tujuan. Admin akan menyiapkan penawaran sesuai barang yang dapat dipenuhi.' },
    ],
  },
  'COMET-THEO-KN95': {
    slug: 'masker-comet-theo-kn95-box-20-pcs',
    id: '1b17d022-8a3a-4066-8f03-e71df3bc684c',
    category: 'Masker',
    parentHref: '/masker-comet',
    parentLabel: 'Masker COMET',
    eyebrow: 'Grosir masker per box',
    summary: 'Theo KN95 memakai konstruksi 5 ply dan model earloop dengan nose wire. Satu box berisi 20 pcs. Warna yang dicari dapat dicantumkan saat meminta stok.',
    buyingNote: 'Dijual per box isi 20 pcs. Minimum order untuk tipe ini dikonfirmasi admin sebelum quotation.',
    facts: [
      { label: 'Isi kemasan', value: '20 pcs per box' },
      { label: 'Konstruksi', value: '5 ply, earloop' },
      { label: 'Data filtrasi', value: 'BFE 98%' },
      { label: 'Nomor izin', value: 'AKD 20903220825' },
    ],
    fitTitle: 'Pertimbangan untuk pengadaan',
    fit: ['Hitung kebutuhan dalam box berdasarkan jumlah pemakai dan frekuensi penggantian.', 'Pilihan warna yang tercantum: hitam, putih, abu, dan krem. Konfirmasikan stok warna.', 'Minta dokumen produk jika fasilitas Anda mensyaratkan izin edar atau standar tertentu.'],
    checkTitle: 'Periksa sebelum PO',
    checks: [
      'Data BFE 98% menjelaskan hasil uji filtrasi bakteri. Cocokkan spesifikasi dengan persyaratan fasilitas Anda.',
      'Konfirmasikan warna, jumlah box, dan stok aktual untuk tipe KN95.',
      'Minta salinan dokumen dan masa berlakunya jika diperlukan untuk pengadaan.',
    ],
    detailLabel: 'Warna atau dokumen yang dicari',
    detailPlaceholder: 'Contoh: hitam, perlu dokumen AKD',
    messageDetail: 'Warna atau dokumen',
    messageRequest: 'Mohon cek stok tipe KN95, minimum order, dokumen yang tersedia, dan quotation.',
    unit: 'box',
    quantityPlaceholder: 'Isi jumlah box',
    alternatives: [
      { label: 'COMET Theo 3 Ply', href: '/produk/masker-comet-theo-3-ply-earloop-box-50-pcs', description: 'Isi 50 pcs per box untuk kebutuhan masker earloop 3 ply.' },
      { label: 'Lini masker COMET', href: '/masker-comet', description: 'Bandingkan Theo KN95 dengan Theo 3 Ply, 4D, dan 4 Ply Carbon.' },
    ],
    faq: [
      { q: 'Berapa isi dan minimum order Theo KN95?', a: 'Satu box berisi 20 pcs. Minimum order mengikuti tipe dan dikonfirmasi admin sebelum quotation.' },
      { q: 'Apakah warna hitam, putih, abu, dan krem tersedia?', a: 'Keempat warna tercantum untuk tipe Theo KN95. Ketersediaan tiap warna perlu dicek saat Anda menyampaikan jumlah box.' },
      { q: 'Bisakah meminta dokumen untuk pengadaan?', a: 'Bisa. Sebutkan dokumen yang diwajibkan. Admin akan mencocokkan dokumen dan masa berlakunya dengan SKU Theo KN95 sebelum PO.' },
      { q: 'Apa arti data BFE 98% pada Theo KN95?', a: 'BFE menyatakan efisiensi filtrasi bakteri pada pengujian produk. Cocokkan spesifikasi dan dokumen Theo KN95 dengan persyaratan penggunaan di fasilitas Anda.' },
      { q: 'Data apa yang perlu dikirim untuk quotation Theo KN95?', a: 'Kirim jumlah box, warna yang dicari, kota tujuan, target penerimaan, dan dokumen yang diwajibkan. Admin akan memeriksa stok serta ongkir sebelum menyiapkan quotation.' },
    ],
  },
  'BRA-OREO-223': {
    slug: 'bra-oreo-223',
    id: 'fe9fbedc-7679-4e76-914e-935e1a9adff2',
    category: 'Bra & Lingerie',
    parentHref: '/bra-oreo',
    parentLabel: 'Bra Oreo grosir',
    eyebrow: 'Grosir bra untuk toko',
    summary: 'Bra Oreo nomor model 223 tersedia dalam rentang ukuran 36 sampai 42. Kirim pembagian ukuran dan warna yang dicari agar admin dapat memeriksa stok sebelum menyiapkan penawaran.',
    buyingNote: 'Satuan pemesanan dalam lusin. Minimum order model 223 dan kombinasi ukuran dikonfirmasi admin.',
    facts: [
      { label: 'Nomor model', value: '223' },
      { label: 'Rentang ukuran', value: '36 sampai 42' },
      { label: 'Warna', value: 'Mengikuti stok' },
      { label: 'Minimum order', value: 'Konfirmasi admin' },
    ],
    fitTitle: 'Data untuk stok toko',
    fit: ['Tentukan jumlah total dalam lusin.', 'Tulis pembagian ukuran yang dibutuhkan dari rentang 36 sampai 42.', 'Sebutkan warna yang dicari; admin akan mengecek pilihan yang tersedia.'],
    checkTitle: 'Periksa sebelum PO',
    checks: [
      'Susunan ukuran dan warna dalam satu lusin perlu dikonfirmasi untuk model 223.',
      'Minta foto stok model 223 bila perlu memeriksa detail cup, jahitan, dan warna.',
      'Harga, jumlah minimum, dan ongkir mengikuti stok serta kota tujuan saat quotation dibuat.',
    ],
    detailLabel: 'Pembagian ukuran dan warna yang dicari',
    detailPlaceholder: 'Contoh: ukuran 36 dan 38, warna hitam',
    messageDetail: 'Ukuran dan warna',
    messageRequest: 'Mohon cek kombinasi ukuran, warna, stok model 223, minimum order, dan quotation.',
    unit: 'lusin',
    quantityPlaceholder: 'Isi jumlah lusin',
    alternatives: [
      { label: 'Lihat model bra Oreo lainnya', href: '/bra-oreo', description: 'Bandingkan nomor model yang tersedia untuk stok toko Anda.' },
    ],
    faq: [
      { q: 'Ukuran apa yang tercantum untuk Bra Oreo 223?', a: 'Rentang ukuran model 223 adalah 36 sampai 42. Kirim pembagian ukuran yang Anda cari agar admin dapat memeriksa stoknya.' },
      { q: 'Berapa minimum order model 223?', a: 'Minimum order dan kemungkinan mencampur ukuran atau warna untuk model 223 dikonfirmasi admin sebelum quotation.' },
      { q: 'Bagaimana cara meminta harga Bra Oreo 223?', a: 'Kirim nomor model 223, jumlah dalam lusin, pembagian ukuran, warna yang dicari, dan kota tujuan melalui WhatsApp. Admin akan memeriksa stok dan menyiapkan quotation.' },
      { q: 'Bisakah ukuran dan warna dicampur dalam satu lusin?', a: 'Kombinasi yang dapat disiapkan untuk model 223 mengikuti stok. Kirim pembagian pcs per ukuran dan warna agar admin dapat memeriksanya sebelum PO.' },
      { q: 'Bisakah meminta foto stok Bra Oreo 223?', a: 'Bisa. Minta foto model 223 dan sebutkan detail yang ingin diperiksa, seperti bentuk cup, jahitan, atau warna. Admin akan mengirim foto barang yang tersedia.' },
    ],
  },
}

export function getProductLandingPilot(product: Pick<StorefrontProduct, 'sku'>) {
  return PILOTS[product.sku] ?? null
}
